/**
 * Public functions for fetching front-facing building polygon coordinates
 * Used in the public-facing InteractiveBuildingFacade component
 */
import { supabase } from '@/integrations/supabase/client';
import type { MapCoordinates } from '@/types/map';

export interface FrontFacingPolygon {
  id: string;
  block: 'A' | 'B';
  unit_number: string;
  unit_id: string;
  map_coordinates: MapCoordinates;
  view_type: string;
  created_at: string;
  updated_at: string;
}

/** Block B view IDs for tabbed views */
export type BlockBViewId = 'northside' | 'southside' | 'center';

/** Single image section for Block B multi-view (tabbed: Northside/Southside/Center) */
export interface ImageSection {
  id: BlockBViewId;
  url: string;
  position: { x: number; y: number };
  dimensions: { width: number; height: number };
}

/** Exterior render config: single image (Block A or fallback) or multi-image (Block B) */
export type ExteriorRenderConfig =
  | { type: 'single'; url: string }
  | {
      type: 'multi-image';
      sections: ImageSection[];
      totalDimensions: { width: number; height: number };
    };

/**
 * Get all front-facing polygons for public display
 * Optionally filtered by block
 */
export async function getFrontFacingPolygons(
  block?: 'A' | 'B'
): Promise<{ data: FrontFacingPolygon[] | null; error: string | null }> {
  try {
    let query = (supabase as any)
      .from('front_facing_polygons')
      .select('*')
      .order('block', { ascending: true })
      .order('unit_number', { ascending: true });

    if (block) {
      query = query.eq('block', block);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching front-facing polygons:', error);
      return { data: null, error: error.message };
    }

    return { data: (data || []) as FrontFacingPolygon[], error: null };
  } catch (error: unknown) {
    console.error('Error fetching front-facing polygons:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { data: null, error: errorMessage };
  }
}

/**
 * Get exterior render image URL for a specific block
 */
export async function getExteriorRenderUrl(block: 'A' | 'B'): Promise<string | null> {
  try {
    const key = `exterior_render_block_${block.toLowerCase()}_url`;
    const { data, error } = await supabase
      .from('site_config')
      .select('value')
      .eq('key', key)
      .single();

    if (error) {
      console.error(`Error fetching exterior render URL for block ${block}:`, error);
      return null;
    }

    return data?.value || null;
  } catch (error) {
    console.error(`Error in getExteriorRenderUrl for block ${block}:`, error);
    return null;
  }
}

/**
 * Get both exterior render URLs
 */
export async function getExteriorRenderUrls(): Promise<{ blockA: string | null; blockB: string | null }> {
  try {
    const [blockA, blockB] = await Promise.all([
      getExteriorRenderUrl('A'),
      getExteriorRenderUrl('B'),
    ]);

    return { blockA, blockB };
  } catch (error) {
    console.error('Error in getExteriorRenderUrls:', error);
    return { blockA: null, blockB: null };
  }
}

const BLOCK_B_LAYOUT_KEY = 'exterior_render_block_b_layout';

/** Map legacy section ids to tabbed view ids */
function normalizeBlockBSectionId(id: string): BlockBViewId {
  const lower = id?.toLowerCase();
  if (lower === 'left') return 'southside';
  if (lower === 'right') return 'northside';
  if (lower === 'center' || lower === 'northside' || lower === 'southside') return lower as BlockBViewId;
  return 'center';
}

const BLOCK_B_VIEW_ORDER: BlockBViewId[] = ['southside', 'center', 'northside'];

function parseBlockBLayout(value: string | null): ExteriorRenderConfig | null {
  if (!value || value.trim() === '') return null;
  try {
    const parsed = JSON.parse(value) as {
      type?: string;
      sections?: (Omit<ImageSection, 'id'> & { id?: string })[];
      totalDimensions?: { width: number; height: number };
    };
    if (
      parsed?.type === 'multi-image' &&
      Array.isArray(parsed.sections) &&
      parsed.sections.length > 0 &&
      parsed.totalDimensions?.width != null &&
      parsed.totalDimensions?.height != null
    ) {
      const raw = parsed.sections
        .filter(
          (s) =>
            s?.id != null &&
            s?.url &&
            typeof s.position?.x === 'number' &&
            typeof s.position?.y === 'number' &&
            typeof s.dimensions?.width === 'number' &&
            typeof s.dimensions?.height === 'number'
        )
        .map((s) => ({ ...s, id: normalizeBlockBSectionId(s.id!) } as ImageSection));
      if (raw.length === 0) return null;

      // Normalize positions so sections are non-overlapping (required for getPolygonSection).
      // Order: southside @ 0,0; center @ (w1,0); northside @ (w1+w2,0).
      const byId = new Map(raw.map((s) => [s.id, s]));
      let offsetX = 0;
      const sections: ImageSection[] = BLOCK_B_VIEW_ORDER.map((id) => {
        const s = byId.get(id);
        if (!s) return null;
        const position = { x: offsetX, y: 0 };
        offsetX += s.dimensions.width;
        return { ...s, position };
      }).filter((s): s is ImageSection => s != null);

      if (sections.length > 0) {
        return {
          type: 'multi-image',
          sections,
          totalDimensions: parsed.totalDimensions,
        };
      }
    }
  } catch {
    // invalid JSON or shape
  }
  return null;
}

/**
 * Determine which image section contains the polygon (by polygon center).
 * Used for multi-image Block B to associate polygons with northside/southside/center views.
 * Polygon coordinates are in the same combined space as section positions (southside at 0,0;
 * center at (sectionWidth, 0); northside at (2*sectionWidth, 0)). Section positions/dimensions
 * in the layout config must match what the admin tracer uses when saving polygons.
 */
export function getPolygonSection(
  polygon: FrontFacingPolygon,
  sections: ImageSection[]
): string | null {
  const coords = polygon.map_coordinates as { type?: string; points?: number[][] } | null;
  if (!coords?.points || coords.points.length === 0) return null;

  const centerX =
    coords.points.reduce((sum, p) => sum + p[0], 0) / coords.points.length;
  const centerY =
    coords.points.reduce((sum, p) => sum + p[1], 0) / coords.points.length;

  for (const section of sections) {
    const { position, dimensions } = section;
    if (
      centerX >= position.x &&
      centerX < position.x + dimensions.width &&
      centerY >= position.y &&
      centerY < position.y + dimensions.height
    ) {
      return section.id;
    }
  }
  return null;
}

/**
 * Get exterior render config for a block.
 * Block A: always returns single image config.
 * Block B: returns multi-image config if exterior_render_block_b_layout is set and valid; otherwise single image fallback.
 */
export async function getExteriorRenderConfig(
  block: 'A' | 'B'
): Promise<ExteriorRenderConfig | null> {
  try {
    if (block === 'A') {
      const url = await getExteriorRenderUrl('A');
      return url ? { type: 'single', url } : null;
    }

    const { data: layoutRow, error: layoutError } = await supabase
      .from('site_config')
      .select('value')
      .eq('key', BLOCK_B_LAYOUT_KEY)
      .single();

    if (!layoutError && layoutRow?.value) {
      const multi = parseBlockBLayout(layoutRow.value);
      if (multi) return multi;
    }

    const url = await getExteriorRenderUrl('B');
    return url ? { type: 'single', url } : null;
  } catch (error) {
    console.error(`Error in getExteriorRenderConfig for block ${block}:`, error);
    return null;
  }
}
